import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { useSeo } from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

const ORIGIN = "https://tatoys.cn";

export default function TopicsPage() {
  const [active, setActive] = useState(categories[0].slug);
  const current = categories.find((c) => c.slug === active) ?? categories[0];
  const list = useMemo(
    () => articles.filter((a) => a.categorySlug === active),
    [active],
  );

  useSeo({
    title: "内容专题库 - 综艺/影视/舞台/造型话题合集｜黑料在线",
    description:
      "黑料在线专题库，按综艺名场面、影视风向、舞台现场、红毯造型、音乐热歌与网络热梗六个方向整理话题合集，支持一键切换专题，快速浏览重点结论。",
    keywords: "黑料在线专题,娱乐话题合集,综艺名场面,影视风向,舞台现场",
    path: "/topics",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        url: `${ORIGIN}/topics`,
        name: "内容专题库",
        inLanguage: "zh-CN",
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: `${ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: "专题库", item: `${ORIGIN}/topics` },
          ],
        },
      },
    ],
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <section className="w-full border-b border-background-200 px-4 py-10 md:px-8 md:py-14">
          <Breadcrumbs items={[{ label: "首页", path: "/" }, { label: "专题库" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">
            内容专题库
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            六个专题方向覆盖娱乐圈的主要话题类型。左侧选择专题，右侧直接浏览该方向下的全部速览内容。
          </p>
        </section>

        <section className="w-full px-4 py-8 md:px-8 md:py-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
            <aside className="lg:sticky lg:top-24 lg:self-start">
              <h2 className="mb-3 font-heading text-sm font-semibold text-foreground-950">选择专题</h2>
              <div className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
                {categories.map((c) => {
                  const isActive = c.slug === active;
                  const count = articles.filter((a) => a.categorySlug === c.slug).length;
                  return (
                    <button
                      key={c.slug}
                      type="button"
                      onClick={() => setActive(c.slug)}
                      className={`flex shrink-0 items-center justify-between gap-3 rounded-md border px-4 py-3 text-left text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                        isActive
                          ? "border-primary-300 bg-primary-50 text-primary-700"
                          : "border-background-200 bg-background-50 text-foreground-700 hover:border-primary-200"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <i className={c.icon} />
                        {c.name}
                      </span>
                      <span className="text-xs text-foreground-500">{count}</span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 hidden rounded-card border border-accent-200 bg-accent-50 p-5 lg:block">
                <i className="ri-megaphone-line text-xl text-accent-700" />
                <h3 className="mt-2 font-heading text-sm font-semibold text-foreground-950">没找到想要的？</h3>
                <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
                  告诉我们你想看的话题方向，编辑会优先安排。
                </p>
                <Link
                  to="/contact"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-label text-accent-800 cursor-pointer hover:underline"
                >
                  推荐话题
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </aside>

            <div>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {current.name}
                </h2>
                <span className="text-sm text-foreground-500">共 {list.length} 篇速览</span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{current.desc}</p>

              {list.length === 0 ? (
                <div className="mt-8 rounded-card border border-dashed border-background-300 py-14 text-center">
                  <i className="ri-inbox-archive-line text-2xl text-foreground-400" />
                  <p className="mt-2 text-sm text-foreground-500">该专题即将上新，先去热榜看看别的话题。</p>
                  <Link
                    to="/hot"
                    className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-label text-background-50 cursor-pointer"
                  >
                    打开热榜
                  </Link>
                </div>
              ) : (
                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {list.map((a) => (
                    <ArticleCard key={a.id} article={a} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}